import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MessageSquare, Send, Smile } from 'lucide-react';
import EmojiPicker from 'emoji-picker-react';
import { useTranslation } from 'react-i18next';

interface ReachOutModalProps {
  isOpen: boolean;
  onClose: () => void;
  cvId: string;
  onSubmitSuccess: (conversationId: string) => void;
  apiCall: (url: string, options: RequestInit) => Promise<Response>;
}

interface ReachOutData {
  cv_id: string;
  initial_message: string;
}

const ReachOutModal: React.FC<ReachOutModalProps> = ({ 
  isOpen, 
  onClose, 
  cvId,
  onSubmitSuccess,
  apiCall 
}) => {
  const { t } = useTranslation('common');
  const [reachOutData, setReachOutData] = useState<ReachOutData>({
    cv_id: cvId,
    initial_message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showMessageEmojiPicker, setShowMessageEmojiPicker] = useState(false);
  const messageRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = async () => {
    if (!reachOutData.initial_message.trim()) {
      setError(t('explore.reachOutModal.errorMessageRequired', 'Please write a message'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const response = await apiCall(`${apiBaseUrl}/api/v1/messaging/reach-out-by-cv`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          cv_id: reachOutData.cv_id,
          initial_message: reachOutData.initial_message,
          is_recruiter_reach_out: false
        }),
      });

      if (!response.ok) {
        // Handle HTTP errors
        const result = await response.json().catch(() => ({ detail: 'Unknown error' }));
        
        if (response.status === 429) {
          setError(t('explore.reachOutModal.errorRateLimit'));
        } else if (response.status === 404) {
          setError(t('explore.reachOutModal.errorContactDisabled'));
        } else {
          setError(result.detail || t('explore.reachOutModal.errorSendFailed'));
        }
        return;
      }

      const result = await response.json();
      
      if (result.id) {
        onSubmitSuccess(result.id);
        onClose();
        // Reset form
        setReachOutData({
          cv_id: cvId,
          initial_message: ''
        });
      } else {
        setError(t('explore.reachOutModal.errorSendFailed'));
      }
    } catch (err: any) {
      console.error('Error sending reach-out:', err);
      
      // Handle specific error cases
      if (err.message && err.message.includes('429')) {
        setError(t('explore.reachOutModal.errorDailyLimit'));
      } else if (err.message && err.message.includes('404')) {
        setError(t('explore.reachOutModal.errorContactDisabled'));
      } else {
        setError(t('explore.reachOutModal.errorSendFailed'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setError(null);
      setShowMessageEmojiPicker(false);
      setReachOutData({
        cv_id: cvId,
        initial_message: ''
      });
      onClose();
    }
  };

  const handleMessageEmojiClick = (emojiData: any) => {
    setReachOutData({ ...reachOutData, initial_message: reachOutData.initial_message + emojiData.emoji });
    setShowMessageEmojiPicker(false);
    messageRef.current?.focus();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[10001] flex items-end sm:items-center justify-center sm:p-4"
            onClick={handleClose}
          >
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 100 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl sm:max-w-lg w-full max-h-[85vh] sm:max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drag handle (mobile) */}
              <div className="flex justify-center pt-2 pb-0 sm:hidden">
                <div className="w-10 h-1 bg-slate-300 rounded-full" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 sm:p-5 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-gradient-to-r from-pink-500 to-pink-600 rounded-lg">
                    <MessageSquare className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h2 className="text-sm sm:text-lg font-bold text-gray-900">{t('explore.reachOutModal.title')}</h2>
                    <p className="text-[11px] sm:text-sm text-gray-500">{t('explore.reachOutModal.subtitle')}</p>
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              {/* Content */}
              <div className="px-4 py-3 sm:p-5 space-y-3 sm:space-y-4">
                {/* Message */}
                <div className="relative">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t('explore.reachOutModal.messageLabel')}
                  </label>
                  <div className="relative">
                    <textarea
                      ref={messageRef}
                      value={reachOutData.initial_message}
                      onChange={(e) => setReachOutData({ ...reachOutData, initial_message: e.target.value })}
                      disabled={isSubmitting}
                      placeholder={t('explore.reachOutModal.messagePlaceholder')}
                      rows={3}
                      maxLength={3000}
                      className="w-full px-3 py-2 sm:py-2.5 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-pink-300 disabled:opacity-50 disabled:cursor-not-allowed resize-none text-sm"
                    />
                    
                    <button
                      type="button"
                      onClick={() => setShowMessageEmojiPicker(!showMessageEmojiPicker)}
                      disabled={isSubmitting}
                      className="absolute right-2 top-2 p-1 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
                    >
                      <Smile className="w-4 h-4 text-gray-400" />
                    </button>

                    {showMessageEmojiPicker && (
                      <div className="absolute bottom-full right-0 mb-2 z-50">
                        <EmojiPicker
                          onEmojiClick={handleMessageEmojiClick}
                          width={300}
                          height={350}
                        />
                      </div>
                    )}
                  </div>
                  {reachOutData.initial_message.length > 2400 && (
                    <p className={`text-[10px] mt-0.5 font-medium ${
                      reachOutData.initial_message.length > 2700 ? 'text-red-600' : 'text-yellow-600'
                    }`}>
                      {reachOutData.initial_message.length}/3000
                    </p>
                  )}
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-2.5 bg-red-50 border border-red-200 rounded-lg"
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="text-red-600 text-xs">⚠️</span>
                      <span className="text-xs text-red-800">{error}</span>
                    </div>
                  </motion.div>
                )}

                <div className="p-2.5 bg-pink-50/60 border border-pink-100 rounded-lg">
                  <p className="text-[11px] sm:text-xs text-gray-600">
                    <strong>{t('explore.reachOutModal.tipTitle')}</strong> {t('explore.reachOutModal.tipMessage')}
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="px-4 py-3 sm:p-4 border-t border-gray-100">
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={handleClose}
                    disabled={isSubmitting}
                    className="px-3 py-2 text-xs sm:text-sm text-gray-600 font-medium rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('explore.reachOutModal.cancelButton')}
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting || !reachOutData.initial_message.trim()}
                    className="flex items-center gap-1.5 px-4 py-2 text-xs sm:text-sm bg-gradient-to-r from-pink-500 to-pink-600 text-white font-medium rounded-lg hover:from-pink-600 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>{t('explore.reachOutModal.sendingButton')}</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-3.5 h-3.5" />
                        <span>{t('explore.reachOutModal.sendButton')}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ReachOutModal;

